/**
 * usePortfolio - Hook for managing portfolio state and API calls.
 *
 * Includes live price refresh on page load for accurate today's P&L.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '@/lib/api';
import type { Position, PortfolioSummary, UploadResult, ImportRecord } from '@/types/portfolio';

export interface StalenessInfo {
  is_stale: boolean;
  days_since_import: number | null;
  last_import_date: string | null;
  message: string;
}

interface UsePortfolioReturn {
  positions: Position[];
  summary: PortfolioSummary | null;
  symbols: string[];
  importHistory: ImportRecord[];
  staleness: StalenessInfo | null;
  isLoading: boolean;
  isUploading: boolean;
  isRefreshingPrices: boolean;
  lastRefreshed: string | null;
  error: string | null;
  uploadFile: (file: File) => Promise<UploadResult | null>;
  refreshPositions: () => Promise<void>;
  refreshPrices: () => Promise<void>;
  clearPositions: () => Promise<void>;
  checkPosition: (symbol: string) => Position | undefined;
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export function usePortfolio(): UsePortfolioReturn {
  const [positions, setPositions] = useState<Position[]>([]);
  const [summary, setSummary] = useState<PortfolioSummary | null>(null);
  const [symbols, setSymbols] = useState<string[]>([]);
  const [importHistory, setImportHistory] = useState<ImportRecord[]>([]);
  const [staleness, setStaleness] = useState<StalenessInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isRefreshingPrices, setIsRefreshingPrices] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<string | null>(
    localStorage.getItem('portfolio_last_refreshed')
  );
  const [error, setError] = useState<string | null>(null);
  const hasAutoRefreshed = useRef(false);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const headers = getAuthHeaders();
      const [posRes, summaryRes, symbolsRes, historyRes, stalenessRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/v1/portfolio/positions`, { headers }),
        fetch(`${API_BASE_URL}/api/v1/portfolio/summary`, { headers }),
        fetch(`${API_BASE_URL}/api/v1/portfolio/symbols`, { headers }),
        fetch(`${API_BASE_URL}/api/v1/portfolio/import-history`, { headers }),
        fetch(`${API_BASE_URL}/api/v1/portfolio/staleness`, { headers }),
      ]);

      if (posRes.ok) setPositions(await posRes.json());
      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (symbolsRes.ok) setSymbols(await symbolsRes.json());
      if (historyRes.ok) setImportHistory(await historyRes.json());
      if (stalenessRes.ok) setStaleness(await stalenessRes.json());
    } catch (err) {
      setError('Failed to load portfolio data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Auto-refresh prices once on page load
  useEffect(() => {
    if (!isLoading && positions.length > 0 && !hasAutoRefreshed.current) {
      hasAutoRefreshed.current = true;
      refreshPricesInternal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, positions.length]);

  const refreshPricesInternal = useCallback(async () => {
    setIsRefreshingPrices(true);
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/v1/portfolio/refresh-prices`, {
        method: 'POST',
        headers,
      });

      if (res.ok) {
        const data = await res.json();
        if (data.summary) {
          setSummary(data.summary);
        }
        const refreshTime = data.last_refreshed || new Date().toISOString();
        setLastRefreshed(refreshTime);
        localStorage.setItem('portfolio_last_refreshed', refreshTime);

        // Re-fetch positions to get updated prices
        const posRes = await fetch(`${API_BASE_URL}/api/v1/portfolio/positions`, { headers });
        if (posRes.ok) setPositions(await posRes.json());
      }
    } catch (err) {
      console.error('Failed to refresh prices:', err);
    } finally {
      setIsRefreshingPrices(false);
    }
  }, []);

  const refreshPrices = useCallback(async () => {
    await refreshPricesInternal();
  }, [refreshPricesInternal]);

  const uploadFile = useCallback(async (file: File): Promise<UploadResult | null> => {
    setIsUploading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      formData.append('file', file);

      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/portfolio/upload`, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Upload failed');
      }

      const result: UploadResult = await response.json();

      // Refresh all data after upload
      await fetchAll();

      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      setError(message);
      return null;
    } finally {
      setIsUploading(false);
    }
  }, [fetchAll]);

  const refreshPositions = useCallback(async () => {
    await fetchAll();
  }, [fetchAll]);

  const clearPositions = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      await fetch(`${API_BASE_URL}/api/v1/portfolio/positions`, {
        method: 'DELETE',
        headers,
      });

      await fetchAll();
    } catch (err) {
      setError('Failed to clear positions');
    }
  }, [fetchAll]);

  const checkPosition = useCallback((symbol: string): Position | undefined => {
    return positions.find(p => p.symbol === symbol.toUpperCase());
  }, [positions]);

  return {
    positions,
    summary,
    symbols,
    importHistory,
    staleness,
    isLoading,
    isUploading,
    isRefreshingPrices,
    lastRefreshed,
    error,
    uploadFile,
    refreshPositions,
    refreshPrices,
    clearPositions,
    checkPosition,
  };
}
